/**
 * Infrastruttura per i GIOCHI PUBBLICI (utenti sloggati).
 *
 * Obiettivo: far girare gli stessi giochi (Oracolo, Chi l'ha detto, Cruciverba)
 * anche senza login, con classifiche e nickname COMPLETAMENTE SEPARATI da quelli
 * degli utenti loggati, SENZA introdurre regressioni.
 *
 * Come:
 *  - Un'app Firebase DEDICATA e ISOLATA ("public-games") con auth ANONIMA e
 *    persistenza in memoria: NON tocca la sessione dell'utente loggato del
 *    portale (auth per-origine).
 *  - Un "gameScope" globale ('logged' | 'public'): i servizi di gioco leggono
 *    questo valore per scegliere il database e il suffisso delle collection
 *    ('' per i loggati, '_public' per i pubblici). Default 'logged' → nessun
 *    cambiamento di comportamento per le pagine esistenti.
 */
import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAuth,
  inMemoryPersistence,
  signInAnonymously,
  type Auth,
} from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};

const APP_NAME = 'public-games';

let publicApp: FirebaseApp | null = null;
let publicAuth: Auth | null = null;
export let dbPublic: Firestore | null = null;

function ensureApp(): boolean {
  if (dbPublic) return true;
  try {
    publicApp = getApps().some((a) => a.name === APP_NAME)
      ? getApp(APP_NAME)
      : initializeApp(firebaseConfig, APP_NAME);
    publicAuth = initializeAuth(publicApp, { persistence: inMemoryPersistence });
    dbPublic = getFirestore(publicApp);
    return true;
  } catch (e) {
    console.warn('[public-games] init fallito:', e);
    return false;
  }
}

let anonUid: string | null = null;
let anonPromise: Promise<string | null> | null = null;

/** Assicura l'auth anonima sull'app pubblica isolata. Ritorna lo uid anonimo. */
export function ensurePublicAuth(): Promise<string | null> {
  if (anonUid) return Promise.resolve(anonUid);
  if (anonPromise) return anonPromise;
  if (!ensureApp() || !publicAuth) return Promise.resolve(null);
  anonPromise = signInAnonymously(publicAuth)
    .then((cred) => { anonUid = cred.user.uid; return anonUid; })
    .catch((e) => { console.warn('[public-games] auth anonima fallita:', e); return null; });
  return anonPromise;
}

// ── Scope globale ────────────────────────────────────────────────────────────
export type GameScope = 'logged' | 'public';
export const gameScope: { value: GameScope } = { value: 'logged' };
export function setGameScope(s: GameScope) { gameScope.value = s; }
